<template>
  <!-- Mobile View -->
  <KsEntityListMobile
    v-if="isMobile"
    :title="t('ksproject.admin.professions.title')"
    :subtitle="t('ksproject.admin.configuration', 'Configuration')"
    :icon="User"
    v-bind="mobileBindings"
    :search-placeholder="t('ksproject.admin.professions.searchPlaceholder')"
    :empty-text="t('ksproject.admin.professions.empty', 'Aucune profession')"
    :delete-title="t('ksproject.admin.professions.deleteTitle')"
    :delete-message-template="t('ksproject.admin.professions.deleteMessageTemplate', 'Voulez-vous vraiment supprimer la profession \'{name}\' ?')"
    :show-fab="canAdd"
    :show-edit-action="canEdit"
    :show-delete-action="canDelete"
    view-route="/admin/professions/{id}"
    @add="goToCreate"
    @edit="goToDetail"
  >
    <template #card="{ item }">
      <KsEntityCard :icon="User" :code="item.code" :title="item.label">
        <template #meta>
          <span>{{ item.defaultHourlyRate != null ? formatCurrency(item.defaultHourlyRate) : '-' }}/h</span>
          <span>{{ item.allowedActivityTypeIds?.length || 0 }} type(s) d'activit&eacute;</span>
        </template>
      </KsEntityCard>
    </template>
  </KsEntityListMobile>

  <!-- Desktop View -->
  <KsListView
    v-else
    v-model:search="searchQuery"
    :title="t('ksproject.admin.professions.title')"
    :subtitle="t('ksproject.admin.professions.subtitle')"
    :add-button-label="t('ksproject.admin.professions.addButton')"
    :search-placeholder="t('ksproject.admin.professions.searchPlaceholder')"
    :show-add-button="canAdd"
    @add="goToCreate"
  >
    <KsTable
      :data="filteredItems"
      :loading="loading"
      :total="store.totalCount"
      :columns="tableColumns"
      card-title-prop="label"
      :show-pagination="false"
      :draggable="canEdit"
      :show-delete-action="canDelete"
      @order-change="handleOrderChange"
      @edit="goToDetail"
      @delete="handleDeleteClick"
    >
      <el-table-column prop="code" :label="t('ksproject.admin.professions.fields.code')" width="150" sortable>
        <template #default="{ row }">
          <KsCodeCell :value="row.code" />
        </template>
      </el-table-column>
      <el-table-column prop="label" :label="t('ksproject.admin.professions.fields.label')" min-width="200" sortable />
      <el-table-column prop="defaultHourlyRate" :label="t('ksproject.admin.professions.fields.defaultHourlyRate')" width="150" align="right">
        <template #default="{ row }">
          <KsCurrencyCell :value="row.defaultHourlyRate" />
        </template>
      </el-table-column>
      <el-table-column :label="t('ksproject.admin.professions.fields.allowedActivityTypes')" min-width="250">
        <template #default="{ row }">
          <el-tag
            v-for="actTypeId in row.allowedActivityTypeIds"
            :key="actTypeId"
            size="small"
            style="margin-right: 4px; margin-bottom: 2px;"
          >
            {{ getActivityTypeLabel(actTypeId) }}
          </el-tag>
          <span v-if="!row.allowedActivityTypeIds?.length">-</span>
        </template>
      </el-table-column>
    </KsTable>
  </KsListView>

  <KsConfirmDialog
    v-model="confirmDelete"
    :title="t('ksproject.admin.professions.deleteTitle')"
    :message="t('ksproject.admin.professions.deleteMessage', 'Voulez-vous vraiment supprimer le métier \'{name}\' ?', { name: itemToDeleteName })"
    type="danger"
    confirm-type="danger"
    :loading="deleting"
    @confirm="confirmDeleteItem"
    @cancel="cancelDelete"
  />
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElTableColumn, ElTag } from 'element-plus'
import { User } from '@element-plus/icons-vue'
import { KsTable, KsListView, KsEntityListMobile, KsEntityCard, KsConfirmDialog, KsCodeCell, KsCurrencyCell, useIsMobile, useCrudListView, type KsTableColumn, useKsI18n, useFormatting } from '@siclop/kstone-vue'
import { usePermissions } from '@siclop/ksadmin-vue'
import { useProfessionStore } from '../stores'
import { useActivityTypeStore } from '@/modules/activity/stores'
import { Profession } from '../models'
import { ProfessionFilter } from '../filters'
import { ProfessionService } from '../services'

const { t } = useKsI18n()
const { isMobile } = useIsMobile()
const { formatCurrency } = useFormatting()
const store = useProfessionStore()
// Actions conditionnees par les droits exiges par l'API (T06). Presentation seulement.
const { canAdd, canEdit, canDelete } = usePermissions('PROFESSION')

const router = useRouter()
// Création en page, avec le formulaire complet de la fiche.
const goToCreate = () => router.push('/admin/professions/new')

const {
  loading,
  confirmDelete, itemToDeleteName, deleting, handleDeleteClick, confirmDeleteItem, cancelDelete,
  searchQuery, filteredItems, mobileBindings, goToDetail, handleOrderChange
} = useCrudListView<Profession, ProfessionFilter>({
  store,
  service: ProfessionService,
  filterClass: ProfessionFilter,
  name: 'profession',
  detailRoute: '/admin/professions',
  orderable: true,
  t
})

const tableColumns = computed<KsTableColumn[]>(() => [
  { prop: 'code', label: t('ksproject.admin.professions.fields.code') },
  { prop: 'label', label: t('ksproject.admin.professions.fields.label') }
])

// Types d'activite (liste deroulante et libelles)
const activityTypeStore = useActivityTypeStore()
const activityTypes = computed(() => activityTypeStore.datas)

function getActivityTypeLabel(actTypeId: number): string {
  const actType = activityTypes.value.find((at) => at.id === actTypeId)
  return actType?.label ?? '-'
}

onMounted(async () => {
  await activityTypeStore.ensureLoaded()
})
</script>
